import { createSelector, createSlice, original, PayloadAction } from '@reduxjs/toolkit';

import { spread } from '@/utils/spread';
import { buildReducerError, getDiffProducts, restoreInitialState } from '@/utils/reducerUtils';
import {
  AddressModel,
  CartAttributeKeys,
  CartModel,
  CartPayModeModel,
  CartShippingModel,
  TopChronoDataModel,
  DeliverySlotDataModel,
  UserModel
} from '@/models';
import { validateDiscountCode } from '@/redux/thunks/checkout/validateDiscountCode';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';
import { addProductToCartFromCheckout } from '@/redux/thunks/checkout/addProductToCartFromCheckout';
import { disconnectUserSuccess } from '@/redux/slices/auth.slice';
import { SlicePayload } from '@/types';
import { RootState } from '@/redux/create-store';
import { deleteProductFromCart } from '@/redux/thunks/deleteProductFromCart';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { removeIncompatibleProductsFromCheckout } from '@/redux/thunks/checkout/removeIncompatibleProducts';

type CartStateStatusValue = { success: true, redirectUrl?: string } | { error: any } | Record<string, never>;

type CartStateStatusKey = 'get' | 'update' | 'partial' | 'pay' | 'validate' | 'add' | 'addOrder' |
  'editWinebox' | 'delete' | 'address' | 'updateAttribute' | 'updateAttributeList';

type CartStateStatus = {
  showConfirm?: boolean;
} & { [key in CartStateStatusKey]?: CartStateStatusValue };

export type CartState = {
  currentlyUpdatingProductsQuantity: boolean;
  content: CartModel;
  last: any[];
  diff: any[];
  status: CartStateStatus;
  fingerprint?: string;
  payjunctionCsr: boolean;
  adminToastsUpdated: boolean;
  checkAdyenPayment: boolean;
  adyenSubmit: boolean;
  topChronoData?: TopChronoDataModel;
  deliverySlotData?: Partial<DeliverySlotDataModel>;
  payModes: CartPayModeModel[];
  isMiniCartOpened: boolean;
}

export const initialState: CartState = {
  currentlyUpdatingProductsQuantity: false,
  content: {
    wineBox: false,
    products: [],
    paiementMode: {}
  } as any,
  last: [],
  diff: [],
  status: {},
  fingerprint: '',
  payjunctionCsr: false,
  adminToastsUpdated: false,
  checkAdyenPayment: false,
  adyenSubmit: false,
  topChronoData: {} as any,
  payModes: [],
  isMiniCartOpened: false,
  deliverySlotData: {} as any
};

type SetCartAddressPayload = {
  storeId: string;
  addressId: string;
  isBillingMode: boolean;
}

const cart = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    // RESET
    resetCart: (state) => {
      restoreInitialState(state, initialState);
    },

    toggleMiniCartOpenedState: (state) => {
      state.isMiniCartOpened = !state.isMiniCartOpened;
    },

    // GET
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getCart: () => {
    },

    getCartSuccess: (state, action: SlicePayload<{ content: CartModel }>) => {
      state.status.get = { success: true };
      saveCart(state, action.payload.content, false);
    },

    getCartError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.get = buildReducerError(action.payload);
    },

    getCartClear: (state) => {
      delete state.status.get;
    },

    // UPDATE
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateCart: (_state, _action: SlicePayload<{ content: CartModel }>) => {
    },

    updateCartSuccess: (state, action: SlicePayload<{ content: CartModel }>) => {
      state.status.update = { success: true };
      saveCart(state, action.payload.content, true);
    },

    updateCartError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.update = buildReducerError(action.payload);
    },

    updateCartClear: (state) => {
      delete state.status.update;
    },

    // PARTIAL
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateCartPartial: (_state, _action: SlicePayload<{ content: Partial<CartModel>, showToast: boolean }>) => {
    },

    updateCartPartialSuccess: (state, action: SlicePayload<{ content: CartModel }>) => {
      state.status.partial = { success: true };
      saveCart(state, action.payload.content, false);
    },

    updateCartPartialError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.partial = buildReducerError(action.payload);
    },

    updateCartPartialClear: (state) => {
      delete state.status.partial;
    },

    // PAY
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    payCart: (_state, _action: SlicePayload<{ content: { cart: CartModel, fingerprint?: string } }>) => {
    },

    payCartSuccess: (state, action: SlicePayload<{ success: any }>) => {
      state.status.pay = { success: action.payload.success };
    },

    payCartError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.pay = buildReducerError(action.payload.error);
    },

    payCartClear: (state) => {
      delete state.status.pay;
    },

    // VALIDATE
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    validateCart: (_state, _action: SlicePayload<{ content: CartModel }>) => {
    },

    validateCartSuccess: (state, action: SlicePayload<{ success: any }>) => {
      state.status.validate = { success: action.payload.success };
    },

    validateCartError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.validate = buildReducerError(action.payload);
    },

    validateCartClear: (state) => {
      delete state.status.validate;
    },

    // PRODUCTS
    addCartProduct:
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    (_state, _action: SlicePayload<{ item: string, qty: number, options?: any, custoText?: string, error?: any }>) => {
    },

    addCartProductSuccess: (state, action: SlicePayload<{ content: CartModel, showConfirm: boolean }>) => {
      const { content, showConfirm } = action.payload;
      state.status.showConfirm = showConfirm;
      state.status.add = { success: true };
      saveCart(state, content, true);
    },

    addCartProductError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.add = buildReducerError(action.payload);
    },

    addCartProductClear: (state) => {
      delete state.status.add;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    editCartWinebox: (_state, _action: SlicePayload<{ boxId: string }>) => {
    },

    editCartWineboxSuccess: (state, action: SlicePayload<{ content: CartModel }>) => {
      state.status.editWinebox = { success: true };
      saveCart(state, action.payload.content, false);
    },

    editCartWineboxError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.editWinebox = buildReducerError(action.payload);
    },

    editCartWineboxClear: (state) => {
      delete state.status.editWinebox;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    addOrderToCart: (_state, _action: SlicePayload<{ content: { storeId: string, orderId: string } }>) => {
    },

    addOrderToCartSuccess: (state) => {
      state.adminToastsUpdated = !state.adminToastsUpdated;
      state.status.addOrder = { success: true };
    },

    addOrderToCartError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.addOrder = action.payload;
    },

    addOrderToCartClear: (state) => {
      delete state.status.addOrder;
    },

    // BOX
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    addCartBox: (_state, _action: SlicePayload<{ boxId: string, qty: number, options?: any }>) => {
    },

    addCartBoxSuccess: (state, action: SlicePayload<{ content: CartModel, showConfirm: boolean }>) => {
      const { content, showConfirm } = action.payload;
      state.status.showConfirm = showConfirm;
      state.status.add = { success: true };
      saveCart(state, content, true);
    },

    addCartBoxError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.add = buildReducerError(action.payload);
    },

    addCartBoxClear: (state) => {
      delete state.status.add;
    },

    // DELETE
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    deleteCart: () => {
    },

    deleteCartSuccess: (state, action: SlicePayload<{ content: CartModel }>) => {
      saveCart(state, action.payload.content, false);
    },

    deleteCartError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.delete = buildReducerError(action.payload);
    },

    deleteCartClear: (state) => {
      delete state.status.delete;
    },

    // FINGERPRINT
    setFingerprint: (state, action: SlicePayload<{ fingerprint: string }>) => {
      state.fingerprint = action.payload.fingerprint;
    },

    resetFingerprint: (state) => {
      state.fingerprint = '';
    },

    setBrowserInfos: (state, action: SlicePayload<{ browserInfo: string }>) => {
      state.content.browserInfo = action.payload.browserInfo;
    },

    // ADDRESS
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setCartAddress: (_state, _action: SlicePayload<{ content: SetCartAddressPayload }>) => {
    },

    setCartAddressSuccess: (state, action: SlicePayload<{ content: CartModel }>) => {
      state.status.address = { success: true };
      saveCart(state, action.payload.content, false);
    },

    setCartAddressError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.address = buildReducerError(action.payload);
    },

    setCartAddressClear: (state) => {
      delete state.status.address;
    },

    // ADYEN
    checkAdyenPayment: (state) => {
      state.checkAdyenPayment = true;
    },

    checkAdyenPaymentClear: (state) => {
      state.checkAdyenPayment = false;
    },

    setAdyenSubmit: (state, action: SlicePayload<{ submit: boolean }>) => {
      state.adyenSubmit = action.payload.submit;
    },

    // PAYJUNCTION CSR
    redirectPayjunctionCsr: (state) => {
      state.payjunctionCsr = true;
    },

    redirectPayjunctionCsrClear: (state) => {
      state.payjunctionCsr = false;
    },

    // UPDATE ATTRIBUTE
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateCartAttribute: (_state, _acion: SlicePayload<{ attributeName: string, newValue: any }>) => {
    },

    updateCartAttributeSuccess: (state, action: SlicePayload<{ content: CartModel }>) => {
      state.status.updateAttribute = { success: true };
      saveCart(state, action.payload.content, true);
    },

    updateCartAttributeError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.updateAttribute = buildReducerError(action.payload);
    },

    updateCartAttributeClear: (state) => {
      delete state.status.updateAttribute;
    },

    // UPDATE ATTRIBUTE LIST
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateCartAttributeList: (_state, _action: SlicePayload<{ attributeList: any[] }>) => {
    },

    updateCartAttributeListSuccess: (state, action: SlicePayload<{ content: CartModel, showConfirm?: boolean }>) => {
      state.status.updateAttributeList = { success: true };
      saveCart(state, action.payload.content, true);
    },

    updateCartAttributeListError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.updateAttributeList = buildReducerError(action.payload);
    },

    updateCartAttributeListClear: (state) => {
      delete state.status.updateAttributeList;
    },

    // TOP CHRONO
    updateTopChronoData: (state, action: SlicePayload<{ data: TopChronoDataModel }>) => {
      state.topChronoData = action.payload.data;
    },

    // DELIVERY SLOT
    updateDeliverySlotData: (state, action: SlicePayload<{ data: Partial<DeliverySlotDataModel> }>) => {
      state.deliverySlotData = action.payload.data;
    },

    // PAYMODES
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getPaymodes: (_state, _action: SlicePayload<{ storeId: string, isApplePayEnabled: boolean }>) => {
    },

    setPaymodes: (state, action: SlicePayload<{ payModes: CartPayModeModel[] }>) => {
      state.payModes = action.payload.payModes;
    },

    // UPDATE DELETED ADDRESS
    updateCartDeletedAddress: (state, action: SlicePayload<{ addressId: string }>) => {
      if (action.payload.addressId === state.content.shipping?.idNext) {
        state.content.shipping = undefined;
      }
      if (action.payload.addressId === state.content.billing?.idNext) {
        state.content.billing = undefined;
      }
    },

    updateCartAddresses: (state, action: SlicePayload<{ user: UserModel }>) => {
      if (state.content.shipping || state.content.billing) {
        action.payload.user.addresses.forEach((address) => {
          if (address.idNext === state.content.shipping?.idNext) {
            state.content.shipping = address;
          }
          if (address.idNext === state.content.billing?.idNext) {
            state.content.billing = address;
          }
        });
      } else {
        state.content.shipping = action.payload.user.addresses?.[0];
        state.content.billing = action.payload.user.addresses?.[0];
      }
    },

    updateShippingModeInsideCart: (state, action: PayloadAction<{ shippingMode: CartShippingModel }>) => {
      state.content.shippingMode = action.payload.shippingMode;
    },

    updateAddressInsideCart: (state, action: PayloadAction<{ address: AddressModel, addressType: 'billing' | 'shipping' }>) => {
      if (action.payload.addressType === 'shipping') {
        state.content.shipping = action.payload.address;
      }
      if (action.payload.addressType === 'billing') {
        state.content.billing = action.payload.address;
      }
    },

    updateAttributeInsideCart: (state, action: PayloadAction<Record<CartAttributeKeys, unknown>>) => {
      const [key, value] = Object.entries(action.payload)[0];
      state.content[key] = value;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(validateDiscountCode.fulfilled, (state, action: SlicePayload<CartModel>) => {
      saveCart(state, action.payload, false);
    });

    builder.addCase(validateDiscountCode.rejected, (state) => {
      state.content.discountCode = undefined;
    });

    builder.addCase(updateCartAttributeByName.pending, (state, action) => {
      const { attributeName, newValue } = action.meta.arg as { attributeName: CartAttributeKeys, newValue: unknown };
      if (attributeName === 'shipping' || (attributeName === 'billing' && !isObjectEmpty(newValue))) {
        const { newValue: address } = action.meta.arg;
        state.content[attributeName] = address;
      }
    });

    builder.addCase(updateCartAttributeByName.fulfilled, (state, action) => {
      state.content = action.payload;
    });

    builder.addCase(addProductToCartFromCheckout.pending, (state) => {
      state.currentlyUpdatingProductsQuantity = true;
    });

    builder.addCase(addProductToCartFromCheckout.rejected, (state) => {
      state.currentlyUpdatingProductsQuantity = false;
    });

    builder.addCase(addProductToCartFromCheckout.fulfilled, (state, action) => {
      state.currentlyUpdatingProductsQuantity = false;
      saveCart(state, action.payload, true);
    });

    builder.addCase(disconnectUserSuccess, (state) => {
      restoreInitialState(state, initialState);
    });

    builder.addCase(deleteProductFromCart.fulfilled, (state, action) => {
      state.currentlyUpdatingProductsQuantity = false;
      saveCart(state, action.payload, true);
      state.last = [];
    });

    builder.addCase(deleteProductFromCart.rejected, (state) => {
      state.currentlyUpdatingProductsQuantity = false;
      state.content.products = state.last;
      state.last = [];
    });

    builder.addCase(removeIncompatibleProductsFromCheckout.fulfilled, (state, action) => {
      saveCart(state, action.payload, true);
    });
  }
});

const saveCart = (state: CartState, newContent: CartModel, computeDiff: boolean): void => {
  if (newContent) {
    // The incoming cart's ID is different from the stored cart ID or is deleted
    // newCart is `true` unless the IDs are the same
    const newCart = (newContent.id || state.content.id) && !!(newContent.id && newContent.id !== state.content.id);

    state.content = {
      ...newContent,
      paiementMode: newContent.paiementMode || {} as CartPayModeModel,
      newCart,
      browserInfo: state.content.browserInfo // Never erase the stored value
    };

    if (computeDiff) {
      state.diff = getDiffProducts(state.content.products, original(state.last));
    }

    state.last = spread(newContent?.products ?? []);
  }
};

export const {
  resetCart, updateAddressInsideCart,
  updateAttributeInsideCart,
  getCart, getCartSuccess, getCartError, getCartClear,
  updateCart, updateCartSuccess, updateCartError, updateCartClear,
  updateCartPartial, updateCartPartialSuccess, updateCartPartialError, updateCartPartialClear,
  payCart, payCartSuccess, payCartError, payCartClear,
  validateCart, validateCartSuccess, validateCartError, validateCartClear,
  addCartProduct, addCartProductSuccess, addCartProductError, addCartProductClear,
  editCartWinebox, editCartWineboxSuccess, editCartWineboxError, editCartWineboxClear,
  addOrderToCart, addOrderToCartSuccess, addOrderToCartError, addOrderToCartClear,
  addCartBox, addCartBoxSuccess, addCartBoxError, addCartBoxClear,
  deleteCart, deleteCartSuccess, deleteCartError, deleteCartClear,
  setFingerprint, resetFingerprint, setBrowserInfos,
  setCartAddress, setCartAddressSuccess, setCartAddressError, setCartAddressClear,
  checkAdyenPayment, checkAdyenPaymentClear, setAdyenSubmit,
  redirectPayjunctionCsr, redirectPayjunctionCsrClear,
  updateCartAttribute, updateCartAttributeSuccess, updateCartAttributeError, updateCartAttributeClear,
  updateCartAttributeList, updateCartAttributeListSuccess, updateCartAttributeListError, updateCartAttributeListClear,
  updateTopChronoData, toggleMiniCartOpenedState, updateDeliverySlotData,
  getPaymodes, setPaymodes,
  updateCartDeletedAddress, updateCartAddresses
} = cart.actions;

export default cart.reducer;

const selectCartContentFromSlice = (state: RootState) => state.cart.content;

export const selectNumberOfProductsPerAvailableType = createSelector(
  [selectCartContentFromSlice],
  (cart) => {
    const grouped = cart.products.reduce((acc, cartProduct) => {
      const key = cartProduct.product.item.attributes.in_stock.valueId;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(cartProduct);
      return acc;
    }, {} as Record<string, typeof cart.products>);
    return Object.fromEntries(
      Object.entries(grouped).map(([key, items]) => [key, items.length])
    );
  }
);

const selectCartState = (state: RootState) => state.cart;

export const selectRelevantCartDetailsForCheckout = createSelector(
  [selectCartState],
  (cart) => ({
    shippingAddress: cart.content.shipping,
    billingAddress: cart.content.billing,
    shippingMode: cart.content.shippingMode,
    paymentMode: cart.content.paiementMode,
    shouldBillAsTaxFree: cart.content.isTaxFreeBilling,
    taxRate: cart.content.shippingTaxRate,
    prearrivalShippingMode: cart.content.prearrivalShippingMode,
    primeurShippingMode: cart.content.primeurShippingMode
  })
);

export const selectCartContainsAvailableForDeliveryProducts = (state: RootState) => state.cart.content.products.some(
  (cartProduct) => (
    cartProduct.product.item.attributes.in_stock.valueId !== 'P' && cartProduct.product.item.attributes.in_stock.valueId !== 'PA'
  )
);

export const selectCartContent = createSelector(
  [selectCartState],
  (cart) => cart.content
);

export const selectShippingPriceCSR = createSelector(
  [selectCartState],
  (cart) => cart.content.shippingPriceCSR
);

export const selectShippingAddress = createSelector(
  [selectCartState],
  (cart) => cart.content.shipping
);

export const selectBillingAddress = createSelector(
  [selectCartState],
  (cart) => cart.content.billing
);

export const selectShippingModeFromCart = createSelector(
  [selectCartState],
  (cart) => cart.content.shippingMode
);

export const selectPaymentMode = createSelector(
  [selectCartState],
  (cart) => cart.content.paiementMode
);

export const selectProductQuantityChanges = (state: RootState) => (
  state.cart.currentlyUpdatingProductsQuantity
);
